import { publicErrorLabel } from './presentation';

export interface Store {
  readonly tradeName: string;
  readonly phone: string;
  readonly whatsapp?: string;
  readonly instagram?: string;
  readonly address: {
    readonly street: string;
    readonly number: string;
    readonly neighborhood: string;
    readonly city: string;
    readonly state: string;
    readonly latitude?: number;
    readonly longitude?: number;
  };
  readonly timezone: string;
  readonly open: boolean;
  readonly acceptingOnlineOrders: boolean;
  readonly checkoutAvailable: boolean;
  readonly pickupEnabled: boolean;
  readonly deliveryEnabled: boolean;
  readonly minimumOrderCents: number;
  readonly estimatedMinutes: number;
  readonly message?: string;
  readonly logoUrl?: string;
  readonly map: {
    readonly provider: 'NONE' | 'OPENFREEMAP' | 'CUSTOM_STYLE';
    readonly styleUrl?: string;
  };
}

export interface Category {
  readonly id: string;
  readonly name: string;
  readonly description?: string;
}

export interface Product {
  readonly id: string;
  readonly categoryId: string;
  readonly name: string;
  readonly description: string;
  readonly priceCents: number;
  readonly available: boolean;
  readonly imageUrl?: string;
}

export interface OptionGroup {
  readonly id: string;
  readonly productId: string;
  readonly name: string;
  readonly minQuantity: number;
  readonly maxQuantity: number;
}

export interface ProductOption {
  readonly id: string;
  readonly groupId: string;
  readonly name: string;
  readonly additionalPriceCents: number;
}

export interface RemovableIngredient {
  readonly id: string;
  readonly productId: string;
  readonly name: string;
}

export interface Catalog {
  readonly categories: readonly Category[];
  readonly products: readonly Product[];
  readonly groups: readonly OptionGroup[];
  readonly options: readonly ProductOption[];
  readonly removableIngredients: readonly RemovableIngredient[];
}

export interface DeliveryArea {
  readonly id: string;
  readonly name: string;
  readonly feeCents: number;
}

export interface CheckoutPayload {
  readonly mode: 'PICKUP' | 'DELIVERY';
  readonly customer: { readonly name: string; readonly phone: string };
  readonly items: readonly {
    readonly productId: string;
    readonly quantity: number;
    readonly notes?: string;
    readonly options?: readonly { readonly optionId: string; readonly quantity: number }[];
    readonly removedIngredientIds?: readonly string[];
  }[];
  readonly paymentIntent: 'CASH' | 'PIX' | 'DEBIT_CARD' | 'CREDIT_CARD' | 'OTHER';
  readonly cashChangeForCents?: number;
  readonly notes?: string;
  readonly address?: {
    readonly street: string;
    readonly number: string;
    readonly city: string;
    readonly deliveryAreaId: string;
    readonly complement?: string;
    readonly reference?: string;
    readonly latitude: number;
    readonly longitude: number;
    readonly accuracy?: number;
    readonly confirmedAt: string;
  };
}

interface ApiEnvelope<T> {
  readonly data: T;
}

interface ApiErrorEnvelope {
  readonly error?: { readonly code?: string; readonly message?: string };
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const headers = new Headers(init?.headers);
  headers.set('accept', 'application/json');
  const response = await fetch(`/api/public/v1${path}`, {
    ...init,
    headers
  });
  const body = (await response.json().catch(() => ({}))) as ApiEnvelope<T> & ApiErrorEnvelope;
  if (!response.ok) throw new Error(publicErrorLabel(body.error?.code));
  return body.data;
}

export async function loadPublicData(): Promise<{
  store: Store;
  catalog: Catalog;
  deliveryAreas: readonly DeliveryArea[];
}> {
  const [store, catalog, deliveryAreas] = await Promise.all([
    loadStore(),
    request<Catalog>('/catalog'),
    request<readonly DeliveryArea[]>('/delivery-areas')
  ]);
  return { store, catalog, deliveryAreas };
}

export function loadStore(): Promise<Store> {
  return request<Store>('/store');
}

export function submitOrder(payload: CheckoutPayload, idempotencyKey: string) {
  return request<{
    readonly orderId: string;
    readonly orderNumber: number;
    readonly status: string;
    readonly totalCents: number;
    readonly estimatedMinutes: number;
    readonly trackingToken: string;
    readonly idempotentReplay: boolean;
  }>('/orders', {
    method: 'POST',
    headers: { 'content-type': 'application/json', 'idempotency-key': idempotencyKey },
    body: JSON.stringify(payload)
  });
}

export function trackOrder(orderId: string, trackingToken: string) {
  return request<{
    readonly orderNumber: number;
    readonly status: string;
    readonly totalCents: number;
    readonly updatedAt: string;
  }>(`/orders/${encodeURIComponent(orderId)}`, {
    headers: { 'x-order-token': trackingToken }
  });
}
